import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe2, Building2, Music, Palette, Utensils, Mic, GraduationCap, ArrowRight } from "lucide-react";
import { IMAGES } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";

const CENTRES = [
  { city: "London", country: "United Kingdom", image: IMAGES.geleBlue },
  { city: "Rhode Island", country: "United States", image: IMAGES.womanBeaded },
  { city: "Ontario", country: "Canada", image: IMAGES.womanKenteCrown },
];

const ACTIVITIES = [
  { icon: Music, title: "Concerts", text: "Live performances of Yorùbá music, drumming, and dance that bring tradition to the stage." },
  { icon: Palette, title: "Arts & Sculpture", text: "Exhibitions celebrating Yorùbá visual art, carving, beadwork, and textile heritage." },
  { icon: Utensils, title: "Cooking Demonstrations", text: "Hands-on experiences of Yorùbá cuisine, flavours, and the stories behind the dishes." },
  { icon: Mic, title: "Lectures & Talk Shows", text: "Engaging talks and conversations with scholars, elders, and cultural entrepreneurs." },
];

export default function Project() {
  useSEO(
    "Our Project",
    "ÀsÀNigHT Worldwide is establishing cultural satellite centres in London, Rhode Island, Ontario and beyond — building a global network of cultural entrepreneurs and a multipurpose Yorùbá community complex."
  );

  return (
    <div>
      {/* HERO */}
      <header className="relative min-h-[58vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.festivalCrowd} alt="Yorùbá cultural gathering" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/70 to-night/40" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-16 w-full">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Our Project</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl text-cream leading-tight text-shadow-hero"
          >
            A Global Network of Yorùbá Culture
          </motion.h1>
        </div>
      </header>

      {/* INTRO */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <Reveal>
          <p className="text-2xl md:text-3xl font-serif text-forest leading-relaxed border-l-2 border-gold pl-6 mb-10">
            ÀsÀNigHT Worldwide is in the continued process of establishing satellites in
            different parts of the world.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="prose-yoruba text-lg text-ink-muted space-y-5">
            <p>
              Our aim is to enlarge our network of cultural entrepreneurs, and to showcase the
              multifarious parts of Yorùbá culture — including concerts, arts and sculpture
              exhibitions, cooking demonstrations, lectures, and talk shows.
            </p>
            <p>
              Our immediate objective is to establish centres in London, Rhode Island, Ontario,
              and other major cities of the world — creating welcoming homes for heritage,
              learning, and celebration wherever the Yorùbá spirit reaches.
            </p>
          </div>
        </Reveal>
      </section>

      {/* CENTRES */}
      <section className="bg-forest text-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal>
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft mb-4 flex items-center gap-2">
              <Globe2 className="w-4 h-4" /> Cultural Satellite Centres
            </p>
            <h2 className="font-serif text-4xl md:text-5xl mb-4">Coming to a city near you</h2>
            <p className="text-cream/70 max-w-2xl mb-14 text-lg">
              We are building cultural homes across continents — hubs where the diaspora and the
              curious gather to learn, create, and celebrate.
            </p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {CENTRES.map((c, i) => (
              <Reveal key={c.city} delay={i * 0.08}>
                <div className="group relative rounded-xl overflow-hidden aspect-[4/5] border border-cream/10" data-testid={`centre-${c.city.toLowerCase().replace(/\s/g, "-")}`}>
                  <img src={c.image} alt={c.city} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-night via-night/30 to-transparent" />
                  <div className="absolute bottom-0 p-7">
                    <p className="text-xs uppercase tracking-widest text-gold-soft mb-1">{c.country}</p>
                    <h3 className="font-serif text-3xl text-cream">{c.city}</h3>
                    <p className="text-sm text-cream/60 mt-2">Planned Centre</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVITIES */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4">What We Showcase</p>
          <h2 className="font-serif text-4xl md:text-5xl text-ink max-w-2xl mb-14">
            Living culture, in every form
          </h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {ACTIVITIES.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06}>
              <div className="h-full border border-parchment rounded-xl p-8 bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-forest text-cream mb-6">
                  <a.icon className="w-5 h-5" />
                </span>
                <h3 className="font-serif text-2xl text-ink mb-3">{a.title}</h3>
                <p className="text-ink-muted leading-relaxed">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMMUNITY COMPLEX */}
      <section className="bg-sand border-y border-parchment">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <img src={IMAGES.traditionalGroup} alt="Community learning" className="rounded-xl w-full object-cover aspect-[4/3]" />
              <div className="absolute -bottom-6 -left-6 hidden md:block w-40 h-40 border border-gold/40 rounded-xl -z-0" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <p className="uppercase tracking-[0.25em] text-xs text-gold mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> The Vision
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight mb-6">
              A Multipurpose Community Complex
            </h2>
            <p className="text-ink-muted text-lg leading-relaxed mb-6">
              ÀsÀNigHT Worldwide plans to develop a multipurpose community complex to promote the
              learning of the Yorùbá language, equipped with modern cultural facilities.
            </p>
            <ul className="space-y-3">
              {[
                "Yorùbá language classes for all ages",
                "Performance and exhibition spaces",
                "A library and digital heritage archive",
                "Workshops for artists and cultural entrepreneurs",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-ink-muted">
                  <GraduationCap className="w-5 h-5 text-gold shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <Reveal>
          <h2 className="font-serif text-4xl md:text-5xl text-ink mb-6">
            Become part of the network
          </h2>
          <p className="text-ink-muted text-lg mb-9 max-w-2xl mx-auto">
            Whether you are a cultural entrepreneur, an artist, an educator, or simply a lover of
            heritage — there is a place for you in the ÀsÀNigHT Worldwide family.
          </p>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-8 py-4 rounded-full hover:bg-forest-light transition-colors"
            data-testid="project-about-cta"
          >
            Learn About Us <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      <Newsletter />
    </div>
  );
}
